import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { EligibleLogo } from "@/assets/onboarding/eligible-logo";
import { trackBillingAddressStarted } from "@/features/analytics/trackers/billing-address-started";
import { trackCompletePurchaseClicked } from "@/features/analytics/trackers/complete-purchase-clicked";
import {
  useCheckout,
  useCompleteOnboarding,
  useCreateUserForm,
  useHandlePaymentRedirect,
  useOnboardingUser,
} from "@/features/onboarding/hooks";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import {
  AddressFieldset,
  PaymentFieldset,
} from "@/features/onboarding/sections";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { MissingClientSecretError } from "@/features/payments/utils";
import { LogoLoader, StateField } from "@/shared/components";
import { CATEGORY_SLUG, PAYMENT_METHOD_TYPE } from "@/shared/domains";
import { getAttributionCookie } from "@/shared/lib/attribution-cookie";
import {
  Button,
  Checkbox,
  Collapse,
  errorToast,
  Form,
  Heading,
} from "@/shared/ui";

const PURCHASE_FORM_ID = "choose-treatment-form";

export const PurchaseForm = () => {
  const contactDetails = useOnboardingStore((state) => state.contactDetails);
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const isCallCenter = useOnboardingStore((state) => state.isCallCenter);

  const setContactDetails = useOnboardingStore(
    (state) => state.setContactDetails,
  );

  const navigate = useNavigate();

  const billingAddressTracked = useRef(false);
  const { isVerifying } = useHandlePaymentRedirect({
    formId: PURCHASE_FORM_ID,
  });

  const form = useCreateUserForm({ requireTermsAndConditionsConsent: false });

  const isBillingSameAsShipping = form.watch("isBillingSameAsShipping");

  const user = useOnboardingUser();

  const {
    card: { pay: cardPay },
    klarna: { pay: klarnaPay },
  } = useCheckout({ formId: PURCHASE_FORM_ID });

  const cardPayMutation = useMutation({
    mutationFn: cardPay,
    onSuccess: () => navigate(ONBOARDING_ROUTE.ACCOUNT_PASSWORD),
    onError: errorToast,
  });

  const completeOnboardingMutation = useCompleteOnboarding();

  const klarnaPayMutation = useMutation({
    mutationFn: klarnaPay,
    onError: (error) => {
      if (error instanceof MissingClientSecretError) {
        if (!user) {
          return;
        }

        const everflowData = getAttributionCookie();
        const selectedPriceId =
          selectedCategories[0]?.selectedProduct?.selectedPrice?.id;

        const everflowParams = {
          efTransactionId: everflowData?._ef_transaction_id ?? "",
          affid: everflowData?.affid ?? "",
          oid: everflowData?.oid ?? "",
          prices: selectedPriceId ? [selectedPriceId] : [],
        };

        return completeOnboardingMutation.mutate({
          code: user.code,
          state: user.state,
          shipping: user.shipping,
          phoneNumber: user.phoneNumber,
          everflowParams,
          isCallCenter,
        });
      }

      errorToast(error.message);
    },
  });

  const handlePay = form.handleSubmit((formData) => {
    if (contactDetails) {
      setContactDetails({
        ...contactDetails,
        shipping: formData.shipping,
        billing: formData.billing ?? formData.shipping,
        state: formData.state,
      });
    }

    const paymentMethodType = formData.paymentMethodType;

    if (paymentMethodType === PAYMENT_METHOD_TYPE.CARD) {
      trackCompletePurchaseClicked({
        user,
        selectedCategories: useOnboardingStore.getState().selectedCategories,
        paymentMethod: paymentMethodType,
      });
      return cardPayMutation.mutate();
    }

    if (paymentMethodType === PAYMENT_METHOD_TYPE.KLARNA) {
      return klarnaPayMutation.mutate();
    }
  });

  return (
    <div className="flex flex-col items-center gap-3">
      <LogoLoader
        show={
          cardPayMutation.isPending ||
          completeOnboardingMutation.isPending ||
          isVerifying
        }
        fullscreen
      >
        {t`We are processing your payment, just give us a moment. You'll be able to create your LevelsRx account shortly!`}
      </LogoLoader>

      <LogoLoader show={klarnaPayMutation.isPending} fullscreen>
        {t`Almost there — we're redirecting you to Klarna to complete your payment`}
      </LogoLoader>
      <Form.Provider {...form}>
        <form id={PURCHASE_FORM_ID} onSubmit={handlePay}>
          <Heading
            size="small"
            level={3}
            color="primary"
            weight="medium"
            align="center"
          >
            {t`Shipping information`}
          </Heading>

          <StateField
            className="mf-masked mf-listen"
            contentClassName="mf-masked mf-listen"
            control={form.control}
            categorySlug={CATEGORY_SLUG.WEIGHT_LOSS}
            onValueChange={() => {
              if (billingAddressTracked.current) {
                return;
              }
              billingAddressTracked.current = true;
              trackBillingAddressStarted({ userId: user?.email });
            }}
          />

          <AddressFieldset form={form} type="shipping" />

          <Form.Field
            control={form.control}
            name="isBillingSameAsShipping"
            render={({ field: { value, onChange, ...rest } }) => (
              <Form.Item id="billing-same-as-shipping" className="mb-8">
                <Form.Control>
                  <Checkbox.Item
                    checked={value}
                    onCheckedChange={(checked) => {
                      onChange(checked);
                      form.clearErrors("billing");
                      form.setValue("billing", undefined);
                    }}
                    {...rest}
                  >
                    <Checkbox.Label>
                      {t`Billing address is the same as shipping`}
                    </Checkbox.Label>
                  </Checkbox.Item>
                </Form.Control>
              </Form.Item>
            )}
          />

          <AnimatePresence>
            {!isBillingSameAsShipping && (
              <Collapse key="address-fieldset" className="col-span-full">
                <AddressFieldset
                  form={form}
                  type="billing"
                  title={t`Billing address`}
                />
              </Collapse>
            )}
          </AnimatePresence>

          <PaymentFieldset onClick={handlePay} />

          <Button
            size="lg"
            type="submit"
            className="w-full"
            onClick={() =>
              form.setValue("paymentMethodType", PAYMENT_METHOD_TYPE.CARD)
            }
          >
            {t`Pay with Card`}
          </Button>
        </form>
      </Form.Provider>

      <EligibleLogo className="w-40" />
    </div>
  );
};
